import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { injectCheckImpersonation } from '@rd/core/services/impersonation/impersonation.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { TermsLinkHelper } from '@rd/helpers/terms-link.helper';
import { LoyaltyProgram } from '@rd/types';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

@Component({
  selector: 'rd-tcv2-review-form',
  templateUrl: './review-form.component.html',
  styleUrls: ['./review-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TcV2ReviewFormComponent implements OnInit {
  @HostBinding('class.rd-tcv2-review-form') componentClass = true;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() pointsTransferLoading: boolean;
  @Input() promocodeLoading = false;
  @Output() submitEvent = new EventEmitter<void>();

  reviewFormGroup: UntypedFormGroup;
  isFormInvalidOnSubmit: boolean;
  privacyPolicyUrl: URL;
  tncsUrl: URL;
  shouldRenderReviewTnc: boolean;
  privacyStatementLink: URL;

  constructor(
    private termsLinkHelper: TermsLinkHelper,
    private urlUtilsService: UrlUtilsService,
    private translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {}

  ngOnInit(): void {
    this.shouldRenderReviewTnc =
      this.transferConnectV2SettingService.shouldRenderReviewTnc;

    this.reviewFormGroup = this.fb.group({
      acceptTnc: [
        false,
        this.shouldRenderReviewTnc ? Validators.requiredTrue : null,
      ],
    });

    this.tncsUrl = this.urlUtilsService.tncsUrl();
    this.privacyPolicyUrl = this.urlUtilsService.privacyPolicyUrl();
    this.privacyStatementLink = this.urlUtilsService.privacyStatementUrl();
  }

  termsTranslationParams(): { [key: string]: string } {
    const loyaltyProgramTncUrl =
      this.loyaltyProgram.pointsTransferCapability.conditionsUrl ?? '';
    const loyaltyProgramTncText = this.translateService.instant(
      'transfer_v2.review.loyalty_program_tnc',
      { loyaltyProgramName: this.loyaltyProgram.name },
    );

    const ascendaTncText = this.translateService.instant(
      'transfer_v2.review.terms_and_conditions',
    );

    const privacyPolicyText = this.translateService.instant(
      'transfer_v2.review.privacy_policy',
    );

    const privacyStatementLinkText = this.translateService.instant(
      'transfer_v2.review.privacy_statement',
    );

    return {
      loyaltyProgramName: this.loyaltyProgram.name,
      loyaltyProgramTncLink: this.termsLinkHelper.createTermsLink({
        termsLinkText: loyaltyProgramTncText,
        // @ts-ignore
        termsLinkUrl: loyaltyProgramTncUrl,
      }),
      ascendaTncLink: this.termsLinkHelper.createTermsLink({
        termsLinkText: ascendaTncText,
        termsLinkUrl: this.tncsUrl,
      }),
      privacyPolicyLink: this.termsLinkHelper.createTermsLink({
        termsLinkText: privacyPolicyText,
        termsLinkUrl: this.privacyPolicyUrl,
      }),
      privacyStatementLink: this.termsLinkHelper.createTermsLink({
        termsLinkText: privacyStatementLinkText,
        termsLinkUrl: this.privacyStatementLink,
      }),
    };
  }

  emitSubmitEventWithImpersonateGuard = injectCheckImpersonation(() => {
    this.submitEvent.emit();
  });

  emitSubmitEvent(): void {
    this.isFormInvalidOnSubmit = this.reviewFormGroup.invalid;
    if (!this.isFormInvalidOnSubmit) {
      this.emitSubmitEventWithImpersonateGuard();
    }
  }

  invalidFormClass(): string | undefined {
    if (this.isFormInvalidOnSubmit) {
      return 'invalid-form';
    }
  }

  showErrorMessage(): boolean {
    return this.isFormInvalidOnSubmit && this.reviewFormGroup.invalid;
  }
}
